<template>
  <section class="cover-view" v-scroll-nav="{ path: '/' }">
    <section class="section cover full row" :style="{ 'background-color': currentColor }">
      <transition name="fade" appear>
        <div class="overlay loader-wrap" v-if="!loaded">
          <Loader />
        </div>
      </transition>
      <section class="preload-images" v-if="!loaded">
        <img
          v-for="(state, index) in states"
          :key="index"
          :src="getImageOrientation(state.image)"
        />
      </section>
      <div class="title col-5">
        <div class="overlay">
          <transition-group name="slide" tag="div" class="text-slide">
            <p
              class="cover-text tx-wg-bold"
              v-for="text in currentText.left"
              :key="text[$i18n.locale]"
            >
              {{ text[$i18n.locale] }}
            </p>
          </transition-group>
        </div>
        <div class="graph-container">
          <AreaGraph
            ref="graph"
            class="graph"
            :data="data"
            v-if="data.length && loaded"
            progressive
            :interactive="false"
            :holes="1"
          />
        </div>
      </div>
      <transition name="slide-horizontal" appear>
        <div
          class="images col-5"
          :style="{
            'background-image': `url(${getImageOrientation(currentState.image)})`
          }"
          v-if="loaded"
        >
          <div>
            <transition-group name="slide" tag="div" class="text-slide">
              <p
                class="cover-text tx-wg-bold"
                v-for="text in currentText.right"
                :key="text[$i18n.locale]"
              >
                {{ text[$i18n.locale] }}
              </p>
            </transition-group>
            <router-link to="/about" v-slot="{ href, navigate }">
              <div
                class="enter-button"
                :class="{ active: finished }"
                :href="href"
                @click="navigate"
              >
                <p class="tx-page-subtitle tx-wg-medium">
                  {{ $t('enter') }}
                </p>
                <div class="enter-icon-wrap">
                  <svg width="33" height="32" viewBox="0 0 33 32" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:class="'enter-icon'" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M14.684 20.776l6.123-4.936-6.123-4.944" stroke="#fff" stroke-width="1.5" stroke-miterlimit="10"/><path d="M16.745 30.836c8.284 0 15-6.716 15-15 0-8.284-6.716-15-15-15-8.284 0-15 6.716-15 15 0 8.284 6.716 15 15 15z" stroke="#fff" stroke-width="1.5" stroke-miterlimit="10" stroke-linecap="round"/></svg>
                </div>
              </div>
            </router-link>
          </div>
        </div>
      </transition>
    </section>
  </section>
</template>

<script>
import AreaGraph from '@/components/common/graphs/AreaGraph';
import { random, sample } from '@/utils';
import { palette, states } from '@/assets/data/cover';
import imageMixin from '@/plugins/image-mixin';
import Loader from '@/components/common/Loader';

export default {
  name: 'Cover',
  components: {
    AreaGraph,
    Loader
  },
  mixins: [imageMixin],
  data() {
    return {
      data: [],
      states,
      current: 0,
      loaded: false,
      finished: false,
      currentText: {
        left: [],
        right: []
      },
      currentState: states[0],
      currentColor: '#F8D238'
    };
  },
  mounted() {
    if (document.readyState === 'complete') this.init();
    else window.addEventListener('load', this.init.bind(this));
    this.data = [];
    for (let i = 0; i < 33; i++) {
      let color = sample(palette);
      if (i % 8 === 0) color = '#051B5B';
      const element = { name: i, color, value: random(15, 260) };
      this.data.push(element);
    }
  },
  methods: {
    init() {
      this.loaded = true;
      this.currentState = this.states.shift();
      this.$nextTick(this.setState);
      this.changeInterval = setInterval(() => {
        if (this.states.length) {
          this.currentState = this.states.shift();
          this.setState();
        } else {
          clearInterval(this.changeInterval);
          this.finished = true;
        }
      }, 2500);
    },
    setState() {
      this.currentText.left.pop();
      this.currentText.left.push(this.currentState.left);
      setTimeout(() => {
        this.currentText.right.pop();
        this.currentText.right.push(this.currentState.right);
      }, 250);
      if (this.$refs.graph) this.$refs.graph.next(5);
    }
  }
};
</script>

<style lang="scss" scoped>
.cover {
  .loader-wrap {
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: $col-yellow-light;
    z-index: 9999;
  }
  .cover-text {
    font-size: 4rem;
    @media screen and (max-width: $mq-tablet) {
      font-size: 3.5rem;
    }
    @media screen and (max-width: $mq-mobile) {
      font-size: 2.5rem;
    }
    @media screen and (max-width: $mq-mobile-small) {
      font-size: 2rem;
    }
  }
  .preload-images {
    position: absolute;
    top: 0;
    left: 0;
    pointer-events: none;
    opacity: 0;
  }
  .images,
  .overlay {
    div.text-slide {
      position: relative;
      display: flex;
      justify-content: center;
      align-items: center;
      height: 100%;
      width: 100%;
      p {
        width: 100%;
        text-align: center;
        position: absolute;
      }
    }
  }
  .matter-container {
    height: 100%;
  }
  .images,
  .title {
    position: relative;
  }
  .images {
    background-position: center;
    background-size: cover;
    background-repeat: no-repeat;
    transition: background-image 0.5s;
    & > div {
      background-color: rgba(0, 0, 0, 0.1);
      width: 100%;
      height: 100%;
    }
  }
  .graph-container {
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    .graph {
      height: 75%;
    }
  }
  .enter-button {
    cursor: pointer;
    position: absolute;
    bottom: 1rem;
    @media screen and (max-width: $mq-mobile) {
      bottom: 0.5rem;
    }
    left: 50%;
    transform: translateX(-50%);
    text-align: center;
    .enter-icon-wrap {
      cursor: pointer;
      text-align: center;
    }
    &.active {
      .enter-icon-wrap {
        animation: bounce 1s infinite;
      }
    }
    p {
      cursor: pointer;
    }
    svg {
      margin-top: $padding/4;
      height: 2.5rem;
      width: 2.5rem;
      transform: rotate(90deg);
    }
  }
}
</style>
